/**
 * Size guide table and tab web components.
 */
(function () {
  let instanceCounter = 0;

  const SELECTORS = {
    tableBody: '.size-guide__table-body',
    tableWrap: '.size-guide__table-wrap',
    unitToggleWrap: '.size-guide__unit-toggle-wrap',
    tableCell: '.size-guide__table-cell',
    tableHeader: '.size-guide__table-header',
    tableRowHeader: '.size-guide__table-row-header',
    tableRow: '.size-guide__table-row',
    tab: '.size-guide__tab',
  };

  const CLASSES = {
    tabActive: 'size-guide__tab--active',
    rowHovered: 'size-guide__table-row--hovered',
    colHovered: 'size-guide__table-header--hovered-col',
    rowHeaderColHovered: 'size-guide__table-row-header--hovered-col',
    cellColHovered: 'size-guide__table-cell--hovered-col',
    cellHovered: 'size-guide__table-cell--hovered',
  };

  function parseJsonAttribute(element, name, fallback) {
    const raw = element.getAttribute(name);

    if (!raw) {
      return fallback;
    }

    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('size-guide-table: invalid JSON for', name, error);

      return fallback;
    }
  }

  function hasSecondaryContent(matrix) {
    if (!Array.isArray(matrix) || matrix.length === 0) {
      return false;
    }

    return matrix.some((row) => Array.isArray(row) && row.some((cell) => cell !== '' && cell != null));
  }

  class SizeGuideTable extends HTMLElement {
    constructor() {
      super();

      this.instanceId = `size-guide-table-${++instanceCounter}`;
      this.showUnitToggle = this.dataset.showUnitToggle === 'true';
      this.columns = parseJsonAttribute(this, 'data-columns', []);
      this.rows = parseJsonAttribute(this, 'data-rows', []);
      this.primaryContent = parseJsonAttribute(this, 'data-content', []);
      this.secondaryContent = parseJsonAttribute(this, 'data-content-2', []);
      this.useSecondary = false;
      this.unitToggle = null;
      this.unitStatus = null;
      this.tableBody = null;
      this.boundMouseOver = this.handleMouseOver.bind(this);
      this.boundMouseOut = this.handleMouseOut.bind(this);
      this.boundUnitChange = this.handleUnitChange.bind(this);
    }

    connectedCallback() {
      this.tableBody = this.querySelector(SELECTORS.tableBody);

      if (!this.tableBody) {
        return;
      }

      const hasSecondary = hasSecondaryContent(this.secondaryContent);

      if (this.showUnitToggle && hasSecondary) {
        this.mountUnitToggle();
      }

      this.tableBody.addEventListener('mouseover', this.boundMouseOver);
      this.tableBody.addEventListener('mouseout', this.boundMouseOut);
    }

    disconnectedCallback() {
      if (this.tableBody) {
        this.tableBody.removeEventListener('mouseover', this.boundMouseOver);
        this.tableBody.removeEventListener('mouseout', this.boundMouseOut);
      }

      if (this.unitToggle) {
        this.unitToggle.removeEventListener('change', this.boundUnitChange);
      }
    }

    mountUnitToggle() {
      const container = this.querySelector(SELECTORS.tableWrap);

      if (!container || container.querySelector(SELECTORS.unitToggleWrap)) {
        return;
      }

      const toggleId = `${this.instanceId}-unit-switch`;
      const statusId = `${this.instanceId}-unit-status`;
      const switchMarkup = document.createElement('div');

      switchMarkup.className = 'size-guide__unit-toggle-wrap';
      switchMarkup.innerHTML = `
        <label class="size-guide__unit-toggle" for="${toggleId}">
          <input
            type="checkbox"
            id="${toggleId}"
            aria-label="${this.dataset.unitToggleLabel || 'Centimeters or inches displayed'}"
            aria-describedby="${statusId}"
          >
          <span class="size-guide__unit-toggle-slider">
            <span>${this.dataset.unitPrimary || 'cm'}</span>
            <span>${this.dataset.unitSecondary || 'in.'}</span>
          </span>
        </label>
        <span class="visually-hidden" id="${statusId}"></span>
      `;

      container.insertBefore(switchMarkup, container.firstChild);

      this.unitToggle = switchMarkup.querySelector('input');
      this.unitStatus = switchMarkup.querySelector('.visually-hidden');
      this.unitToggle.addEventListener('change', this.boundUnitChange);
      this.updateUnitStatus();
    }

    handleUnitChange() {
      this.useSecondary = Boolean(this.unitToggle && this.unitToggle.checked);
      this.updateCellValues();
      this.updateUnitStatus();
    }

    updateUnitStatus() {
      if (!this.unitStatus) {
        return;
      }

      const showingSecondary = this.useSecondary;
      const primaryLabel = this.dataset.unitPrimary || 'cm';
      const secondaryLabel = this.dataset.unitSecondary || 'inches';
      const template = showingSecondary
        ? this.dataset.unitStatusSecondary || 'Currently showing {{ unit }}'
        : this.dataset.unitStatusPrimary || 'Currently showing {{ unit }}';

      this.unitStatus.textContent = template
        .replace('{{ unit }}', showingSecondary ? secondaryLabel : primaryLabel);
    }

    updateCellValues() {
      const matrix = this.useSecondary ? this.secondaryContent : this.primaryContent;
      const cells = this.tableBody.querySelectorAll(SELECTORS.tableCell);

      cells.forEach((cell) => {
        const rowIndex = Number(cell.dataset.rowIndex);
        const colIndex = Number(cell.dataset.colIndex);
        const value = matrix[rowIndex] && matrix[rowIndex][colIndex] != null
          ? matrix[rowIndex][colIndex]
          : '';

        cell.textContent = value;
      });
    }

    handleMouseOver(event) {
      const cell = event.target.closest('td, th');

      if (!cell || !this.tableBody.contains(cell)) {
        return;
      }

      const row = cell.closest('tr');
      const colIndex = cell.cellIndex;

      this.clearHoverState();

      if (row) {
        row.classList.add(CLASSES.rowHovered);
      }

      this.querySelectorAll(`${SELECTORS.tableRowHeader}, ${SELECTORS.tableHeader}, ${SELECTORS.tableCell}`).forEach((element) => {
        if (element.cellIndex !== colIndex) {
          return;
        }

        if (element.matches(SELECTORS.tableRowHeader)) {
          element.classList.add(CLASSES.rowHeaderColHovered);

          return;
        }

        if (element.matches(SELECTORS.tableHeader)) {
          element.classList.add(CLASSES.colHovered);

          return;
        }

        element.classList.add(CLASSES.cellColHovered);
      });

      if (cell.matches(SELECTORS.tableCell)) {
        cell.classList.add(CLASSES.cellHovered);
      }
    }

    handleMouseOut(event) {
      const related = event.relatedTarget;

      if (related && this.tableBody.contains(related)) {
        return;
      }

      this.clearHoverState();
    }

    clearHoverState() {
      this.querySelectorAll(
        `.${CLASSES.rowHovered}, .${CLASSES.colHovered}, .${CLASSES.rowHeaderColHovered}, .${CLASSES.cellColHovered}, .${CLASSES.cellHovered}`,
      ).forEach((element) => {
        element.classList.remove(
          CLASSES.rowHovered,
          CLASSES.colHovered,
          CLASSES.rowHeaderColHovered,
          CLASSES.cellColHovered,
          CLASSES.cellHovered,
        );
      });
    }
  }

  class SizeGuideTabs extends HTMLElement {
    constructor() {
      super();

      this.tabs = [];
      this.panels = [];
      this.activeIndex = 0;
      this.boundTabClick = this.handleTabClick.bind(this);
      this.boundTabKeydown = this.handleTabKeydown.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll(SELECTORS.tab));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

      if (this.tabs.length === 0) {
        this.panels.forEach((panel) => {
          panel.hidden = false;
          panel.style.display = 'block';
        });

        return;
      }

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', this.boundTabClick);
        tab.addEventListener('keydown', this.boundTabKeydown);

        if (tab.classList.contains(CLASSES.tabActive) || tab.getAttribute('aria-selected') === 'true') {
          this.activeIndex = index;
        }
      });

      this.activateTab(this.activeIndex, false);
    }

    disconnectedCallback() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.boundTabClick);
        tab.removeEventListener('keydown', this.boundTabKeydown);
      });
    }

    handleTabClick(event) {
      const tab = event.currentTarget;
      const index = this.tabs.indexOf(tab);

      if (index === -1) {
        return;
      }

      event.preventDefault();
      this.activateTab(index, true);
    }

    handleTabKeydown(event) {
      const tab = event.currentTarget;
      const index = this.tabs.indexOf(tab);

      if (index === -1) {
        return;
      }

      let nextIndex = index;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (index + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activateTab(nextIndex, true);
      this.tabs[nextIndex].focus();
    }

    activateTab(index, focusPanel) {
      this.activeIndex = index;

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;

        tab.classList.toggle(CLASSES.tabActive, isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;

        panel.hidden = !isActive;
        panel.style.display = isActive ? 'block' : 'none';
        panel.setAttribute('tabindex', '-1');

        if (isActive && focusPanel) {
          panel.focus();
        }
      });
    }
  }

  if (!customElements.get('size-guide-table')) {
    customElements.define('size-guide-table', SizeGuideTable);
  }

  if (!customElements.get('size-guide-tabs')) {
    customElements.define('size-guide-tabs', SizeGuideTabs);
  }
})();
